import {
  CreditCardStatus,
  IssuerCardProjectionStatus,
  IssuerCardStatusReason,
  type IssuerCardStatusResponse,
  PhysicalCardStatus,
  PhysicalCardStatusReason,
} from 'src/models/creditCard.models'
import type {
  CapabilityAvailability,
  CardLimitsProvenance,
  CardLimitsResponse,
  CardManagementCapabilitiesResponse,
} from 'src/queries/physicalCardManagement/physicalCardManagement.models'

import { Money, PhysicalCardManagementStatus } from './cardDetailHeroTypes'

/**
 * Pure mappers between the wire shapes and the shape the hero regions read.
 *
 * They live outside `useCardDetailHeroData` on purpose: the regrouping and the
 * status mapping are the two places this slice can silently misreport a card,
 * so they are unit-testable without React Query, a store, or a rendered tree.
 */

/** The identity group. Sourced from `GET /creditcards/{id}`. */
export type CardDetailHeroIdentity = {
  nickname: string
  cardholder: { displayName: string }
  last4: string
  cardArt: {
    /**
     * Issuer artwork from the credit-card detail API. `medium` (592x370), not
     * `large` (1600x1000): the face renders at 300px CSS wide, so `large` is
     * ~7x the pixels for no visible gain. `AccountHeader` picks `medium` too.
     */
    imageUrl: string
    /** API-provided contrast styling for credentials over the issuer artwork. */
    textColor: string
    textShadow: string
  }
  /**
   * Used only for the card-art accessible name. The credit-card detail response
   * carries no card-network field, so this is the issuer code — the nearest real
   * value. See the PR notes.
   */
  network: string
  /**
   * `undefined` when the backend status has no card-management counterpart.
   * Deliberately not defaulted: showing a wrong pill misreports the card.
   */
  status: PhysicalCardManagementStatus | undefined
  /**
   * Real expiry from the credit-card detail response. The reveal carries its own
   * copy on the card-details payload; this one belongs to the identity group and
   * is available without opening the reveal at all.
   */
  expiry: string
}

/**
 * The limits group, regrouped from the flat wire response. The wire sends one
 * `currency` for the whole payload; the hero's `Money` carries it per value, so
 * it is fanned out here. Same minor units on both sides — a regrouping, never a
 * conversion.
 */
export type CardDetailHeroLimits = {
  permanentCardSpendControl: { limit: Money }
  cardFinancials: { balance: Money; availableSpend: Money }
  /**
   * Carried through the seam although nothing renders it: the limit-change
   * mutation slice sends it as `expectedVersion`, and dropping it here would
   * force that slice to re-fetch.
   */
  version: number
  /**
   * `SURROGATE_PARTNER` values are really persisted and really versioned, but
   * they are not real issuer balances — the UI must not present them as
   * authoritative.
   */
  provenance: CardLimitsProvenance
}

export const toCardDetailHeroLimits = (
  response: CardLimitsResponse,
): CardDetailHeroLimits => ({
  permanentCardSpendControl: {
    limit: {
      amountMinor: response.permanentLimit,
      currency: response.currency,
    },
  },
  cardFinancials: {
    balance: {
      amountMinor: response.currentBalance,
      currency: response.currency,
    },
    // Deliberately not clamped: the contract preserves a negative available
    // spend on purpose (an over-limit card), and zeroing it would misreport it.
    availableSpend: {
      amountMinor: response.availableSpend,
      currency: response.currency,
    },
  },
  version: response.version,
  provenance: response.provenance,
})

/**
 * The physical card's own status, mapped into card-management vocabulary.
 *
 * LOCKED is issuer-imposed and is NOT a voluntary FROZEN — its action is
 * "Request unlock", so conflating the two misreports what the cardholder can do
 * about it.
 */
const fromPhysicalCardStatus = (
  status: PhysicalCardStatus,
  statusReason: PhysicalCardStatusReason | null,
): PhysicalCardManagementStatus | undefined => {
  switch (status) {
    case PhysicalCardStatus.Active:
      return 'ACTIVE'
    case PhysicalCardStatus.Pending:
    case PhysicalCardStatus.NeedsVerification:
    case PhysicalCardStatus.NeedsCVSVerification:
    case PhysicalCardStatus.EnrollmentAvailable:
      // All four are "no usable plastic yet, and something still has to happen
      // before there is" — the pending-issuance state.
      return 'PENDING_ISSUANCE'
    case PhysicalCardStatus.Inactive:
      // A deliberately deactivated card: reversible, holder-visible — FROZEN.
      return 'FROZEN'
    case PhysicalCardStatus.Blocked:
      // Issuer-imposed, clears only through an unlock request.
      return 'LOCKED'
    case PhysicalCardStatus.Closed:
    case PhysicalCardStatus.Expired:
      return 'CANCELED'
    case PhysicalCardStatus.NotApplicable:
      // NOT_APPLICABLE alone just means "no physical card here", which is not a
      // card state at all — only the failure reasons make it one.
      return statusReason !== null ? 'FAILED' : undefined
    default:
      // A status the API added after this mapping was written. Better to render
      // no pill than a confidently wrong one.
      return undefined
  }
}

/** Account-level fallback, used only when the card carries no physical status. */
const fromCreditCardStatus = (
  status: CreditCardStatus,
): PhysicalCardManagementStatus | undefined => {
  switch (status) {
    case CreditCardStatus.Active:
      return 'ACTIVE'
    case CreditCardStatus.Pending:
      return 'PENDING_ISSUANCE'
    case CreditCardStatus.Inactive:
      return 'FROZEN'
    case CreditCardStatus.Blocked:
      return 'LOCKED'
    case CreditCardStatus.Closed:
    case CreditCardStatus.Expired:
      return 'CANCELED'
    case CreditCardStatus.NotApplicable:
      // The physical-only account marker, not a card state.
      return undefined
    default:
      return undefined
  }
}

/**
 * Maps the stored issuer projection without pretending its deliberately coarse
 * `BLOCKED` value is a more specific state than the attached reason proves.
 */
const fromIssuerCardStatus = (
  issuerCardStatus: IssuerCardStatusResponse,
): PhysicalCardManagementStatus | undefined => {
  if (issuerCardStatus.status === IssuerCardProjectionStatus.Active) {
    return issuerCardStatus.reason === null ? 'ACTIVE' : undefined
  }

  if (issuerCardStatus.status !== IssuerCardProjectionStatus.Blocked) {
    return undefined
  }

  switch (issuerCardStatus.reason) {
    case IssuerCardStatusReason.Frozen:
      return 'FROZEN'
    case IssuerCardStatusReason.ReplacementPending:
      return 'REPLACEMENT_PENDING'
    default:
      return undefined
  }
}

/** The subset of the credit card the status mapping actually reads. */
export type CardDetailHeroStatusSource = {
  status: CreditCardStatus
  issuerCardStatus?: IssuerCardStatusResponse | null
  physicalCard: {
    status: PhysicalCardStatus | null
    statusReason: PhysicalCardStatusReason | null
  }
}

export const toCardDetailHeroStatus = (
  creditCard: CardDetailHeroStatusSource,
): PhysicalCardManagementStatus | undefined => {
  const issuerStatus =
    creditCard.issuerCardStatus != null
      ? fromIssuerCardStatus(creditCard.issuerCardStatus)
      : undefined

  if (issuerStatus !== undefined) return issuerStatus
  if (creditCard.physicalCard.status !== null) {
    return fromPhysicalCardStatus(
      creditCard.physicalCard.status,
      creditCard.physicalCard.statusReason,
    )
  }
  return fromCreditCardStatus(creditCard.status)
}

/** The subset of the credit card the identity mapping reads. */
export type CardDetailHeroIdentitySource = CardDetailHeroStatusSource & {
  displayName: string
  last4: string
  issuer: string
  expiresFormattedString: string
  cardImage: {
    urls: { medium: string }
    textColorRGBA: string
    hasTextShadow: boolean
    shadowTextColorRGBA?: string
  }
  issuerCardholderName?: string | null
  user?: { firstName?: string; lastName?: string; email: string }
}

/**
 * The contract carries `cardholder.displayName`; the credit card carries a user.
 * Falls back to the email rather than rendering half a name.
 */
const cardholderDisplayName = (
  user: CardDetailHeroIdentitySource['user'],
): string => {
  if (user === undefined) return ''
  return user.firstName !== undefined && user.lastName !== undefined
    ? `${user.firstName} ${user.lastName}`
    : user.email
}

export const toCardDetailHeroIdentity = (
  creditCard: CardDetailHeroIdentitySource,
): CardDetailHeroIdentity => ({
  nickname: creditCard.displayName,
  cardholder: {
    displayName:
      creditCard.issuerCardholderName ?? cardholderDisplayName(creditCard.user),
  },
  last4: creditCard.last4,
  cardArt: {
    imageUrl: creditCard.cardImage.urls.medium,
    textColor: creditCard.cardImage.textColorRGBA,
    textShadow:
      creditCard.cardImage.hasTextShadow &&
      creditCard.cardImage.shadowTextColorRGBA !== undefined
        ? `1px 1px 4px ${creditCard.cardImage.shadowTextColorRGBA}, -1px -1px 4px ${creditCard.cardImage.shadowTextColorRGBA}`
        : 'none',
  },
  network: creditCard.issuer,
  status: toCardDetailHeroStatus(creditCard),
  expiry: creditCard.expiresFormattedString,
})

/**
 * Whether the limits region may read limits at all.
 *
 * Capabilities are per-action, and this is NOT the hero's visibility question.
 * `VIEW_PHYSICAL_CARD_MANAGEMENT` answers that one — as of EX-40599 the page ORs
 * it with the hero's flag — whereas the limits region consults
 * `READ_PHYSICAL_CARD_LIMIT` and nothing else. Keeping them separate is the
 * point: an enrolled card is entitled to the hero even when its limits are
 * unreadable (an unsupported currency, say). A denied capability is a real
 * answer (the query is `retry: false`), not a transient failure.
 */
export const canReadCardLimits = (
  capabilities: CardManagementCapabilitiesResponse,
): boolean => {
  const readLimit = capabilities.capabilities.find(
    (capability) => capability.action === 'READ_PHYSICAL_CARD_LIMIT',
  )
  // A response that does not mention the action at all is not a grant.
  return readLimit !== undefined && readLimit.availability !== 'UNAVAILABLE'
}

/**
 * The server's verdict on revealing card details, plus `UNKNOWN` for "no verdict
 * was given". Distinct from `undefined`, which means the capabilities request has
 * not resolved yet.
 */
export type CardDetailsAvailability = CapabilityAvailability | 'UNKNOWN'

/**
 * Secure-details action lookup. READ_ONLY is a grant, because revealing details
 * is itself a read-only operation.
 *
 * A missing entry returns `UNKNOWN`, NOT `UNAVAILABLE`. The two are different
 * claims and only one of them is ours to make: `UNAVAILABLE` means a backing
 * system was consulted and said no, whereas an absent entry means nobody was
 * asked. Keeping that separate here stops the model from asserting a denial
 * nobody issued.
 *
 * Presentation is a separate decision, and both callers deliberately treat
 * `UNKNOWN` like `UNAVAILABLE`: `CardDisplayActions` hides the reveal trigger
 * and `useCardRevealDisclosure` closes and clears any open reveal. A user cannot
 * act on the difference between "denied" and "never asked" — both mean no
 * details.
 *
 * The reveal has no demo path to fall back to, so no trigger is rendered when
 * this action is missing.
 */
export const getCardDetailsAvailability = (
  capabilities: CardManagementCapabilitiesResponse,
): CardDetailsAvailability =>
  capabilities.capabilities.find(
    (capability) => capability.action === 'READ_PHYSICAL_CARD_SECURE_DETAILS',
  )?.availability ?? 'UNKNOWN'
